<template>
  <div class="p-grid">
    <div class="p-col-12">
      <LayoutCompareNote hint="New Quotation opens in this layout. The layout everyone uses is set in Shared Enquiry Settings." />
      <div v-if="error" class="pse-note error"><i class="pi pi-times-circle"></i><span>{{ error }}</span></div>
      <div v-if="enq" class="card">
        <h4>
          <span class="pse-muted" style="font-weight: normal;">Enquiry:</span> {{ enq.project.name }} <span class="pse-muted" style="font-weight: normal;">· {{ phaseText }}</span>
        </h4>
        <p class="pse-muted" style="margin-top: -8px;">
          {{ [enq.project.postcodeArea, enq.project.postCode, enq.project.town].filter(Boolean).join(' · ') }} · {{ enq.description }}
        </p>
        <div class="pse-row" style="align-items: stretch;">
          <div v-for="v in enq.values || []" :key="v.productType" class="pse-kpi pse-value">
            <div class="label">{{ v.productType }}</div>
            <div class="value">{{ v.n ? money(v.value) : '-' }}</div>
            <div class="hint">{{ valueLabel(v) }}</div>
          </div>
        </div>
      </div>

      <!-- the enquiry's quotations in today's browse grid -->
      <div v-if="enq" class="card">
        <h4>Quotations</h4>
        <Toolbar class="p-mb-4">
          <template slot="left">
            <div class="p-inputgroup">
              <Button icon="pi pi-fw pi-refresh" class="p-mr-1" @click="load" />
              <Button icon="pi pi-fw pi-cog" class="p-mr-1" @click="columnsDialog = true" />
              <InputText v-model="searchText" placeholder="Global Search" />
              <Button icon="pi pi-times p-pr-1" class="p-mr-1" @click="searchText = ''" />
            </div>
          </template>
          <template slot="right">
            <Button v-if="canAdd" label="New Quotation" icon="pi pi-plus" class="p-button-success p-mr-2"
              @click="$router.push({ name: 'newEnquiry', query: { sharedEnqId: String(enq.sharedEnqId), ...viewQuery } })" />
          </template>
        </Toolbar>
        <DataTable :value="rows" dataKey="oppId" :paginator="true" :rows="25" :rowsPerPageOptions="[25, 50, 100]" :rowHover="true"
          class="p-datatable-striped p-datatable-sm p-datatable-gridlines p-datatable-customers" :resizableColumns="true" columnResizeMode="expand"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="{first} to {last} of {totalRecords} Quotations" @row-click="openBid($event.data)">
          <Column header="Actions" headerStyle="width: 50px;">
            <template #body="s">
              <Button icon="pi pi-external-link" class="p-button-rounded p-button-text" title="Open enquiry" @click.stop="openBid(s.data)" />
            </template>
          </Column>
          <Column v-for="c in shownColumns" :key="c.field" :field="c.field" :header="c.header" :sortable="true" :headerStyle="c.style">
            <template #body="s">
              <span v-if="c.field === 'oppStatus'" :class="statusBadge(s.data.oppStatus)">{{ s.data.oppStatus }}</span>
              <span v-else-if="c.money">{{ money(s.data[c.field]) }}</span>
              <span v-else>{{ s.data[c.field] }}</span>
            </template>
          </Column>
        </DataTable>
      </div>

      <div v-if="enq" class="card">
        <div class="pse-row">
          <h4 style="margin: 0;">Enquiry Details</h4>
          <span class="pse-muted">Changing them here updates every quotation on the enquiry.</span>
          <span class="pse-spacer"></span>
          <Button v-if="!editing" label="Edit" icon="pi pi-pencil" class="p-button-outlined" style="width: auto;" @click="editing = true" />
          <template v-else>
            <Button label="Cancel" icon="pi pi-times" class="p-button-danger" style="width: 100px;" @click="cancelEdit" />
            <Button label="Save" icon="pi pi-check" class="p-button-success" style="width: 100px;" :loading="saving" @click="save" />
          </template>
        </div>
        <div class="p-fluid" style="margin-top: 12px;">
          <JobFields v-model="job" :settings="settings" :submitted="submitted" :disabled="!editing" colClass="p-col-12 p-md-6 p-lg-3" />
        </div>
        <div v-if="submitted && missing.length" class="pse-note error pse-missing">
          <i class="pi pi-times-circle"></i><span>Not saved. Please fill in: <strong>{{ missing.join(', ') }}</strong>.</span>
        </div>
        <h5>Attachments</h5>
        <AttachmentsByCategory :sharedEnqId="enq.sharedEnqId" />
      </div>
    </div>
    <GridColumns :visible.sync="columnsDialog" :columns="columns" v-model="chosen" />
  </div>
</template>

<script>
import api from './sharedEnquiryApi'
import JobFields from './JobFields.vue'
import AttachmentsByCategory from './AttachmentsByCategory.vue'
import GridColumns from './GridColumns.vue'
import LayoutCompareNote from './LayoutCompareNote.vue'
import { money, valueLabel, phaseLabel, missingMandatory, errorText, statusBadge } from './sharedEnquiryLogic'
import { addEnquiryLevel, canAddEnquiry } from './openNewEnquiry'
import { viewOf } from './enquiryLayout'

const COLUMNS = [
  { field: 'sQuoteNo', header: 'Quote No', style: 'width: 110px;' },
  { field: 'custName', header: 'Customer' },
  { field: 'productType', header: 'Product Type' },
  { field: 'branch', header: 'Branch' },
  { field: 'salesExec', header: 'Sales Exec' },
  { field: 'dateReceived', header: 'Date Received', style: 'width: 120px;' },
  { field: 'quoteValue', header: 'Quote Value', money: true, style: 'width: 130px;' },
  { field: 'quoteGrade', header: 'Grade', style: 'width: 80px;' },
  { field: 'chance', header: 'Chance', style: 'width: 80px;' },
  { field: 'oppStatus', header: 'Status', style: 'width: 110px;' }
]

// The Enquiry page (Mohammed 18/10, one layout only from 23/10): its quotations in today's browse grid, the details below.
export default {
  name: 'SharedEnquiryClassic',
  components: { JobFields, AttachmentsByCategory, GridColumns, LayoutCompareNote },
  props: {
    id: { type: Number, required: true }
  },
  data() {
    return {
      enq: null, job: {}, settings: [], error: '', searchText: '', columns: COLUMNS, chosen: COLUMNS.map(c => c.field),
      columnsDialog: false, editing: false, saving: false, submitted: false
    }
  },
  computed: {
    canAdd() { return canAddEnquiry(addEnquiryLevel(this.$store)) },
    viewQuery() {
      const view = viewOf(this.$route && this.$route.query)
      return view ? { view } : {}
    },
    phaseText() { return this.enq ? phaseLabel(this.enq.phase ? [this.enq.phase] : [], this.enq.phaseId) : '' },
    shownColumns() { return this.columns.filter(c => this.chosen.includes(c.field)) },
    rows() {
      const q = this.searchText.trim().toLowerCase()
      const bids = (this.enq && this.enq.bids) || []
      return q ? bids.filter(b => this.columns.some(c => String(b[c.field] == null ? '' : b[c.field]).toLowerCase().includes(q))) : bids
    },
    missing() { return missingMandatory(this.settings.filter(s => s.level !== 'bid'), { project: this.enq && this.enq.project, job: this.job }) }
  },
  watch: { id: 'load' },
  mounted() { this.load() },
  methods: {
    money, valueLabel, statusBadge,
    async load() {
      this.error = ''
      try {
        const [enq, settings] = await Promise.all([api.sharedEnquiry(this.id), api.fieldSettings()])
        this.settings = settings || []
        this.enq = enq
        this.cancelEdit()
      } catch (e) {
        this.error = errorText(e, 'Could not load the enquiry')
      }
    },
    jobOf(enq) {
      const { project, phase, bids, values, attachments, ...job } = enq
      return job
    },
    cancelEdit() { this.job = this.jobOf(this.enq); this.editing = false; this.submitted = false },
    async save() {
      this.submitted = true
      if (this.missing.length) return
      this.saving = true
      try {
        this.enq = await api.updateSharedEnquiry(this.enq.sharedEnqId, this.job)
        this.cancelEdit()
      } catch (e) {
        this.error = errorText(e, 'Could not save the enquiry details')
      } finally {
        this.saving = false
      }
    },
    openBid(b) { this.$router.push({ path: '/enquiries/' + b.oppId + '/detail' }) }
  }
}
</script>

<style scoped>
@import './sharedEnquiry.css';
</style>
